import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMyPermissions } from '../../../hooks/useMyPermissions'
import { MODULE_META, ModuleKey } from './lib/moduleMeta'
import { AccessLevel } from './lib/levelMeta'
import ModuleLevelCell from './components/ModuleLevelCell'

export interface ModuleAccessRow { module: string; level: AccessLevel; overridden: boolean; custom: boolean; actions: string[] }
export interface RoleWithPermissions {
  id: string
  key: string | null
  name: string
  isSystem: boolean
  permissions: { module: string; actions: string[] }[]
}
export interface MemberRow {
  projectUserId: string
  user: { id: string; name: string | null; email: string | null; company: string | null; rights: string }
  companyName: string | null
  projectRole: string
  projectRoleId: string | null
  modules: ModuleAccessRow[]
}

interface Props {
  rows: MemberRow[]
  selected: Set<string>
  pending: boolean
  roles: RoleWithPermissions[]
  onToggle: (userId: string) => void
  onToggleAll: () => void
  onChangeRole: (projectUserId: string, roleId: string) => void
  onSetLevel: (userId: string, module: ModuleKey, level: AccessLevel) => void
  onSetActions: (userId: string, module: ModuleKey, actions: string[]) => void
  onReset: (userId: string, module: ModuleKey) => void
  onRemove: (userId: string) => void
  onSetCompany: (userId: string, company: string) => void
  onFolderAccess: (userId: string) => void
}

const CompanyCell: React.FC<{
  value: string | null; pending?: boolean; editable?: boolean; onSave: (v: string) => void
}> = ({ value, pending, editable = true, onSave }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  // Company is a global attribute of the user, not project data — editing it needs
  // org admin, so a project admin sees it read-only instead of hitting a denial.
  if (!editable) {
    return <span className="block truncate text-sm">{value || <span className="text-muted-foreground">—</span>}</span>
  }
  if (!editing) {
    return (
      <button
        type="button"
        disabled={pending}
        className={cn(
          'text-left w-full truncate text-sm hover:underline decoration-dotted',
          pending && 'opacity-60 cursor-wait',
        )}
        onClick={() => { setDraft(value ?? ''); setEditing(true) }}
      >
        {value || <span className="text-muted-foreground">—</span>}
      </button>
    )
  }
  return (
    <Input
      autoFocus
      disabled={pending}
      className="h-7 text-sm"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false)
        const next = draft.trim()
        if (next !== (value ?? '')) onSave(next)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') setEditing(false)
      }}
    />
  )
}

const MembersTable: React.FC<Props> = ({
  rows, selected, pending, roles, onToggle, onToggleAll, onChangeRole, onSetLevel, onSetActions, onReset, onRemove,
  onSetCompany, onFolderAccess,
}) => {
  const { t } = useTranslation()
  const { can } = useMyPermissions()
  const canEditCompany = can('ADMIN', 'ORGANIZATION')
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.user.id))

  const levelOf = (r: MemberRow, key: ModuleKey): ModuleAccessRow =>
    r.modules.find((m) => m.module === key) ?? { module: key, level: 'NONE', overridden: false, custom: false, actions: [] }

  return (
    <div className="rounded-md border border-border overflow-hidden bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="!w-9 !py-0 !pr-0 !pl-2 text-center"><Checkbox checked={allSelected} onCheckedChange={() => onToggleAll()} aria-label="Select all" /></TableHead>
            <TableHead>{t('Members_col_member')}</TableHead>
            <TableHead>{t('Members_col_company')}</TableHead>
            <TableHead>{t('Members_col_role')}</TableHead>
            {MODULE_META.map((m) => (
              <TableHead
                key={m.key}
                className={cn(
                  'text-center whitespace-nowrap',
                  // PROJECT is a management column (project admin), not a content
                  // module — bracket it visually so the two groups read apart.
                  m.key === 'PROJECT' && 'border-l border-r border-border bg-muted/50',
                )}
              >
                {t(m.labelKey, m.key)}
              </TableHead>
            ))}
            <TableHead className="!w-9 !p-0 text-center" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.user.id} data-state={selected.has(r.user.id) ? 'selected' : undefined}>
              <TableCell className="!w-9 !py-0 !pr-0 !pl-2 text-center"><Checkbox checked={selected.has(r.user.id)} onCheckedChange={() => onToggle(r.user.id)} aria-label={`Select ${r.user.name ?? r.user.email}`} /></TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-sm">{r.user.name || r.user.email || '—'}</span>
                  {(r.user.rights === 'OWNER' || r.user.rights === 'ADMIN') && (
                    <Badge
                      variant="secondary"
                      title={t('Members_org_admin_hint')}
                    >
                      {t('rights_' + r.user.rights)}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">{r.user.email}</div>
              </TableCell>
              <TableCell>
                <CompanyCell
                  value={r.user.company}
                  pending={pending}
                  editable={canEditCompany}
                  onSave={(v) => onSetCompany(r.user.id, v)}
                />
              </TableCell>
              <TableCell>
                <Select value={r.projectRoleId ?? ''} onValueChange={(v) => onChangeRole(r.projectUserId, v)} disabled={pending}>
                  <SelectTrigger className="h-8 w-[160px] text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>{role.key ? t('role_' + role.key, role.name) : role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              {MODULE_META.map((m) => {
                const cell = levelOf(r, m.key)
                return (
                  <TableCell
                    key={m.key}
                    className={cn(
                      'text-center',
                      m.key === 'PROJECT' && 'border-l border-r border-border bg-muted/30',
                    )}
                  >
                    <ModuleLevelCell
                      module={m.key}
                      level={cell.level}
                      overridden={cell.overridden}
                      custom={cell.custom}
                      actions={cell.actions}
                      pending={pending}
                      onSetLevel={(lvl) => onSetLevel(r.user.id, m.key, lvl)}
                      onSetActions={(actions) => onSetActions(r.user.id, m.key, actions)}
                      onReset={() => onReset(r.user.id, m.key)}
                      onDrilldown={m.key === 'DOC_CONTROL' ? () => onFolderAccess(r.user.id) : undefined}
                    />
                  </TableCell>
                )
              })}
              <TableCell className="!w-9 !p-0 text-center">
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={pending}
                  onClick={() => onRemove(r.user.id)} aria-label={t('Members_remove')}>
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default MembersTable
